/**
 * tabs.svelte.ts — per-window tab bar state (Task 2).
 *
 * Session identity lives in sessionStorage (per-window, survives F5 but not
 * closing the tab). The full tab list is persisted to the server so a reload
 * restores all tabs exactly as the user left them.
 *
 * Rules:
 *   - View tabs are deduplicated: opening the same view activates the
 *     existing tab, it does not create a second one.
 *   - Card tabs can appear multiple times (different cards).
 *   - save_tabs is debounced 500 ms so rapid tab changes don't hammer
 *     the server.
 */

import { api } from '../api.ts';

// ── Types ──────────────────────────────────────────────────────────────────

export interface TabEntry {
  type:  'view' | 'card';
  key:   string;   // Tab name like "board" or card UUID
  label: string;   // Display text
}

interface OpenTabInput {
  tab_type: string;
  tab_key:  string;
  label:    string;
  position: number;
}

interface OpenTabRow {
  id:           string;
  workspace_id: string;
  session_id:   string;
  tab_type:     string;
  tab_key:      string;
  label:        string;
  position:     number;
  created_at:   string;
}

// ── Session ID ─────────────────────────────────────────────────────────────

function getOrCreateSessionId(): string {
  try {
    let sid = sessionStorage.getItem('agentboard.sessionId');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('agentboard.sessionId', sid);
    }
    return sid;
  } catch {
    // sessionStorage unavailable (e.g. SSR, private browsing with restrictions)
    return crypto.randomUUID();
  }
}

// ── Store ──────────────────────────────────────────────────────────────────

export const tabStore = $state({
  tabs:         [] as TabEntry[],
  activeIdx:    0,
  sessionId:    getOrCreateSessionId(),
  closedStack:  [] as TabEntry[],
});

// ── Debounced save ─────────────────────────────────────────────────────────

let _saveTimer: ReturnType<typeof setTimeout> | null = null;
let _lastWorkspaceId = '';

function scheduleSave(workspaceId: string) {
  _lastWorkspaceId = workspaceId;
  if (_saveTimer !== null) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    _saveTimer = null;
    void flushSave(_lastWorkspaceId);
  }, 500);
}

async function flushSave(workspaceId: string) {
  if (!workspaceId) return;
  const inputs: OpenTabInput[] = tabStore.tabs.map((t, i) => ({
    tab_type: t.type,
    tab_key:  t.key,
    label:    t.label,
    position: i,
  }));
  try {
    await api('/api/v1/tabs', {
      method: 'PUT',
      body: JSON.stringify({
        workspace_id: workspaceId,
        session_id:   tabStore.sessionId,
        tabs:         inputs,
      }),
    });
  } catch (e) {
    console.warn('[tabs] save failed:', e);
  }
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Load the saved tab list from the server for the current session.
 * Call once on mount after the workspace is known.
 */
export async function loadTabs(workspaceId: string): Promise<void> {
  if (!workspaceId) return;
  try {
    const rows = await api<OpenTabRow[]>(
      `/api/v1/tabs?workspace_id=${encodeURIComponent(workspaceId)}&session_id=${encodeURIComponent(tabStore.sessionId)}`
    );
    if (rows && rows.length > 0) {
      tabStore.tabs = rows.map(r => ({
        type:  r.tab_type as 'view' | 'card',
        key:   r.tab_key,
        label: r.label,
      }));
      tabStore.activeIdx = 0;
    }
  } catch (e) {
    console.warn('[tabs] load failed:', e);
  }
}

/**
 * Open a tab. View tabs are deduplicated; card tabs are always appended.
 * Returns the index of the activated tab.
 */
export function openTab(workspaceId: string, type: 'view' | 'card', key: string, label: string): number {
  if (type === 'view') {
    // Deduplicate: if the view is already open, just activate it.
    const existing = tabStore.tabs.findIndex(t => t.type === 'view' && t.key === key);
    if (existing !== -1) {
      // Already active? No-op — prevents $effect infinite loop where
      // activeTab → openTab → tabStore mutation → re-trigger effect.
      if (tabStore.activeIdx === existing) return existing;
      tabStore.activeIdx = existing;
      return existing;
    }
  }

  // For card tabs, also check if this exact card is already the active tab.
  if (type === 'card') {
    const existing = tabStore.tabs.findIndex(t => t.type === 'card' && t.key === key);
    if (existing !== -1) {
      if (tabStore.activeIdx === existing) return existing;
      tabStore.activeIdx = existing;
      return existing;
    }
  }

  const entry: TabEntry = { type, key, label };
  const insertAt = tabStore.activeIdx + 1;
  tabStore.tabs = [
    ...tabStore.tabs.slice(0, insertAt),
    entry,
    ...tabStore.tabs.slice(insertAt),
  ];
  tabStore.activeIdx = insertAt;
  scheduleSave(workspaceId);
  return insertAt;
}

/** Close the tab at the given index. */
export function closeTab(workspaceId: string, idx: number): void {
  if (idx < 0 || idx >= tabStore.tabs.length) return;
  tabStore.closedStack = [...tabStore.closedStack, tabStore.tabs[idx]];
  tabStore.tabs = tabStore.tabs.filter((_, i) => i !== idx);

  // Recalculate active index after removal.
  if (tabStore.tabs.length === 0) {
    tabStore.activeIdx = 0;
  } else if (tabStore.activeIdx >= tabStore.tabs.length) {
    tabStore.activeIdx = tabStore.tabs.length - 1;
  } else if (tabStore.activeIdx > idx) {
    tabStore.activeIdx -= 1;
  }
  scheduleSave(workspaceId);
}

/** Activate the tab at the given index. */
export function activateTab(idx: number): void {
  if (idx >= 0 && idx < tabStore.tabs.length) {
    tabStore.activeIdx = idx;
  }
}

/** Drag-reorder: move tab from `from` to `to`. */
export function reorderTab(workspaceId: string, from: number, to: number): void {
  if (from === to) return;
  const tabs = [...tabStore.tabs];
  const [moved] = tabs.splice(from, 1);
  tabs.splice(to, 0, moved);
  tabStore.tabs = tabs;
  // Keep the active tab the same logical entry after reorder.
  if (tabStore.activeIdx === from) {
    tabStore.activeIdx = to;
  } else if (from < tabStore.activeIdx && to >= tabStore.activeIdx) {
    tabStore.activeIdx -= 1;
  } else if (from > tabStore.activeIdx && to <= tabStore.activeIdx) {
    tabStore.activeIdx += 1;
  }
  scheduleSave(workspaceId);
}

/** Reopen the most recently closed tab (Ctrl+Shift+T). */
export function reopenLastClosed(workspaceId: string): void {
  if (tabStore.closedStack.length === 0) return;
  const entry = tabStore.closedStack[tabStore.closedStack.length - 1];
  tabStore.closedStack = tabStore.closedStack.slice(0, -1);
  openTab(workspaceId, entry.type, entry.key, entry.label);
}

/** Close the active tab (Ctrl+W). */
export function closeActiveTab(workspaceId: string): void {
  closeTab(workspaceId, tabStore.activeIdx);
}

/** Return the currently active TabEntry (or null if empty). */
export function getActiveTab(): TabEntry | null {
  return tabStore.tabs[tabStore.activeIdx] ?? null;
}
